import React from 'react';
import { Alert } from '@mui/material';

/**
 * Why the worklist has no answer to show.
 *
 * - `no-match`: the list loaded and nothing matches the filters.
 * - `timed-out`: the list ran out of time and the backend served an empty
 *   page in its place (`degraded` on the worklist response, GDN-2142).
 * - `failed`: the request failed outright, so there is no answer at all.
 */
export type WorklistEmptyStateReason = 'no-match' | 'timed-out' | 'failed';

export interface WorklistEmptyStateProps {
  reason: WorklistEmptyStateReason;
  /** The request behind this page was limited by a service-date range. */
  hasDateRange: boolean;
}

/**
 * What the Encounters worklist says when it has no answer to show (GDN-4096).
 *
 * Only `no-match` says that nothing matches. A timed-out page says the list
 * did not load and what to change: without a date range, choose one; with
 * one, choose a shorter one. A failed request says the list could not load.
 */
export const WorklistEmptyState: React.FC<WorklistEmptyStateProps> = ({ reason, hasDateRange }) => {
  if (reason === 'failed') {
    return (
      <Alert severity="error">{"The encounter list couldn't load. Try again in a moment."}</Alert>
    );
  }

  if (reason === 'timed-out') {
    return (
      <Alert severity="warning">
        {hasDateRange
          ? 'The encounter list took too long to load for this date range. Choose a shorter range to see encounters.'
          : 'The encounter list took too long to load, so nothing is shown. Choose a date range, such as Last 90 days, to see encounters.'}
      </Alert>
    );
  }

  return <Alert severity="info">No encounters match those filters.</Alert>;
};
